import { CSSProperties, ReactNode, RefObject, useEffect, useId, useMemo, useRef } from 'react';
import { Link } from 'react-router';
import {
  AggregationType,
  Article,
  ArticleVariant,
  PageSearchResult,
  SEARCH_COUNT_CEILING,
  SEARCH_MIN_CHARS,
  SEARCH_SECTION_LIMIT,
  SearchResults,
  StatDefinition,
  StatSource,
} from '@repo/types';
import { useGlobalSearchLazyQuery } from '@data/search/useGlobalSearchQuery';
import { SkeletonLoader } from '@ui/common/components/SkeletonLoader';
import { trackSiteSearch } from '@ui/utils/tracking';
import { ArticleCard } from '@ui/article/ArticleCard';
import { ClubCard } from '@ui/league/ClubCard';
import { PlayerCard } from '@ui/player/components/PlayerCard';
import { SearchIcon } from '../SearchTrigger/SearchIcon';
import { PageCard } from './PageCard';
import { useFocusTrap } from './useFocusTrap';
import { useHeaderBlockHeight } from './useHeaderBlockHeight';
import { useModalSurface } from './useModalSurface';
import { useSearchPages } from './useSearchPages';

const SKELETON_CARD_COUNT = 4;

/**
 * The two stat columns the design's player card shows. Ids match what `SearchSquadService` asks
 * the stats app for, so the values line up with the labels.
 */
/** The same asset `Logo` renders — what marks a Pages result as a page on this site. */
const SITE_LOGO = 'web/site-logo.webp';

const PLAYER_STATS: StatDefinition[] = [
  { id: 'appearances', label: 'Apps', source: StatSource.Matches, aggregation: AggregationType.Count },
  { id: 'goals', label: 'Goals', source: StatSource.Goals, aggregation: AggregationType.Count },
];

export interface GlobalSearchProps {
  /** `drawer` sits at the top of MobileNav; `pane` slides in from the right on desktop. */
  variant?: 'drawer' | 'pane';
  /** @default 'What are you looking for?' */
  placeholder?: string;
  /** `pane` only: whether the pane is on screen. */
  open?: boolean;
  /** `pane` only: close from the scrim, the ✕ or Escape. */
  onClose?: () => void;
  /** Fires when the query crosses the minimum — the drawer swaps its nav list out on this. */
  onActiveChange?: (active: boolean) => void;
  /** Focus the field once the surface is on screen. Named to avoid the DOM `autoFocus` prop: focus
   * is moved imperatively after mount, which is what a dialog should do, rather than by the
   * attribute the a11y rule warns about. */
  focusOnOpen?: boolean;
  id?: string;
}

/**
 * Site-wide search. One component, two surfaces — the mobile nav drawer and the desktop pane that
 * slides in from the right.
 *
 * Results are grouped by type and never interleaved, each section a rail that scrolls when it
 * overruns the surface. Empty sections are omitted entirely: a search with no player matches has no
 * Players heading at all, not an empty one. A search matching nothing gets one empty state for the
 * whole search rather than one per section.
 */
export function GlobalSearch({
  variant = 'drawer',
  placeholder = 'What are you looking for?',
  open = false,
  onClose,
  onActiveChange,
  focusOnOpen = false,
  id,
}: GlobalSearchProps) {
  const generatedId = useId();
  const surfaceId = id ?? generatedId;
  const inputRef = useRef<HTMLInputElement>(null);
  const paneRef = useRef<HTMLDivElement>(null);
  // Only the pane is modal — `MobileNav` owns the drawer's Escape handling and scroll lock, since
  // the drawer sits inside a surface that has already locked the page. Declared up here because the
  // header measurement below is gated on the same condition.
  const isModal = variant === 'pane' && open;
  // The pane is `position: fixed`, so its header-colour block sits beside the site header's own
  // without being anywhere near it in the tree — CSS has no way to ask how tall that block is. The
  // measurement is handed over as a custom property and `global-search.scss` sizes the head from
  // it, which keeps the two blocks level for a tenant whose header is not npl's height. 0 before
  // the first measurement and on the server; the stylesheet's fallback covers that.
  const siteHeaderHeight = useHeaderBlockHeight(isModal);

  const { searchTerm, debouncedTerm, onSearchTermChange, clear, results, isActive, isLoading, isFetching, isError } =
    useGlobalSearchLazyQuery();
  const pages = useSearchPages(debouncedTerm, SEARCH_SECTION_LIMIT);

  const sectionTotals = [results.clubs.total, results.players.total, results.news.total];
  const total = sectionTotals.reduce((sum, sectionTotal) => sum + sectionTotal, 0) + pages.length;
  const hasResults = total > 0;
  // Each section's count is bounded independently, so the sum understates whenever ANY of them hit
  // its ceiling — testing the sum against the ceiling would instead call a genuine 300 "100+".
  const isTotalBounded = sectionTotals.some((sectionTotal) => sectionTotal >= SEARCH_COUNT_CEILING);

  useEffect(() => {
    onActiveChange?.(isActive);
  }, [isActive, onActiveChange]);

  // One event per executed search, carrying the term and the count — a zero-result search is the
  // same event with result_count: 0.
  //
  // Gated on `isFetching`, not `isLoading`. Under `keepPreviousData` a new term keeps the previous
  // data and leaves status 'success', so `isLoading` is false for every search after the first:
  // gating on it fired the event the moment the term changed, while `results` still held the
  // previous search's payload. Every event after the first then carried the preceding term's
  // count, and a zero-result search was reported as non-zero — the one number D12 exists for.
  // `isError` is excluded too, so a failed search is not reported as a completed one.
  useEffect(() => {
    if (isActive && !isFetching && !isError) {
      trackSiteSearch(debouncedTerm, total);
    }
    // `total` stays out of the deps: it is read at the point the data has settled for this term,
    // and including it would fire a second event for the same search.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedTerm, isActive, isFetching, isError]);

  // Escape-to-close and the page scroll lock, for the pane only — see `isModal` above.
  useModalSurface(isModal, onClose);

  // Declared BEFORE the focus effect below, and that ordering is load-bearing: effects run in
  // declaration order, so the trap captures `document.activeElement` while it is still the element
  // that opened the pane. Declared after, it would capture the search input this component had
  // just focused, and "restore" focus to a field inside the pane it was closing.
  useFocusTrap(paneRef, isModal);

  useEffect(() => {
    if (focusOnOpen && (variant !== 'pane' || open)) {
      inputRef.current?.focus();
    }
  }, [focusOnOpen, open, variant]);

  const sections = useMemo(() => buildSections(results, pages, variant, onClose), [results, pages, variant, onClose]);

  const field = (
    <SearchField
      inputRef={inputRef}
      value={searchTerm}
      placeholder={placeholder}
      controls={surfaceId}
      onChange={onSearchTermChange}
      onClear={clear}
    />
  );

  // What a screen reader is told, and the only thing that tells one the search ran at all. The
  // visible copy changes are silent: the body swaps through four renderings — resting copy, the
  // keep-typing hint, the skeleton, then the count and sections — with nothing announced, and the
  // count is the one number that says a search happened.
  //
  // A separate `sr-only` line rather than `aria-live` on the body itself, because the body holds
  // the result rails: a live region around those reads every card back on each keystroke. This
  // carries the one sentence worth hearing, and it is rendered unconditionally so the region is in
  // the DOM before its content changes — a live region introduced together with its text is
  // frequently not announced at all.
  const status = buildStatusMessage({
    isActive,
    isLoading,
    isError,
    hasResults,
    total,
    isTotalBounded,
    term: debouncedTerm,
    searchTerm,
  });

  const body = renderBody({
    variant,
    isActive,
    isLoading,
    isError,
    hasResults,
    total,
    isTotalBounded,
    term: debouncedTerm,
    sections,
    searchTerm,
  });

  const surface = { surfaceId, field, status, body };

  if (variant === 'pane') {
    return open ? (
      <SearchPane {...surface} headerHeight={siteHeaderHeight} paneRef={paneRef} onClose={onClose} />
    ) : null;
  }

  // `--active` is what tells the drawer's stylesheet to hand the search the rest of the panel and
  // become the thing that scrolls. Idle it is the field and nothing else, so the nav list below it
  // keeps the height it needs: sized to fill unconditionally, the search took half a phone screen
  // of empty header colour and pushed the navigation off the bottom of the drawer.
  //
  // It is driven by the same `isActive` that fires `onActiveChange`, but not at the same moment:
  // the class lands in this render, the host unmounts the nav list from the effect that follows,
  // which React runs after paint. `global-search.scss` closes that one-frame window by hiding
  // `.nav__drawer-body` off the class directly — CSS-side, so nothing is ever painted with both.
  return (
    <div className={`global-search global-search--drawer${isActive ? ' global-search--active' : ''}`} role="search">
      <div className="global-search__head">{field}</div>
      <SearchBody {...surface} />
    </div>
  );
}

type SurfaceContent = {
  surfaceId: string;
  field: ReactNode;
  status: string;
  body: ReactNode;
};

/**
 * The scrolling half of both surfaces: the announced state, then whatever `renderBody` drew.
 *
 * The `sr-only` line is rendered unconditionally, and that is the point of it being here rather
 * than inside `renderBody` — a live region added to the DOM at the same moment as its text is
 * frequently not announced at all, so it has to outlive every state it reports on.
 */
function SearchBody({ surfaceId, status, body }: Omit<SurfaceContent, 'field'>) {
  return (
    <div className="global-search__body" id={surfaceId}>
      <p className="sr-only" role="status">
        {status}
      </p>
      {body}
    </div>
  );
}

/**
 * The desktop surface: a scrim over the page and a modal panel sliding in from the right.
 *
 * `headerHeight` is the measured height of the site header's colour block, handed to
 * `global-search.scss` as a custom property so the panel's head lines up with it — see
 * {@link useHeaderBlockHeight}. Omitted entirely when nothing was measured, which is what lets the
 * stylesheet's own fallback apply.
 */
function SearchPane({
  surfaceId,
  field,
  status,
  body,
  headerHeight,
  paneRef,
  onClose,
}: SurfaceContent & {
  headerHeight: number;
  paneRef: RefObject<HTMLDivElement>;
  onClose?: () => void;
}) {
  const style = headerHeight > 0 ? ({ '--site-header-height': `${headerHeight}px` } as CSSProperties) : undefined;

  return (
    <div
      className="global-search global-search--pane"
      role="dialog"
      aria-modal="true"
      // Not "Search this site", which is the input's own label: the dialog and the field inside it
      // announcing the identical string reads as a stutter on open.
      aria-label="Site search"
      style={style}
    >
      <div className="global-search__scrim" onClick={onClose} aria-hidden="true" />
      <div className="global-search__pane" role="search" ref={paneRef}>
        <div className="global-search__head">
          {field}
          <button type="button" className="global-search__close" aria-label="Close search" onClick={onClose}>
            <CloseIcon size={20} />
          </button>
        </div>
        <SearchBody surfaceId={surfaceId} status={status} body={body} />
      </div>
    </div>
  );
}

/** The field itself — identical on both surfaces, so it is written once. */
function SearchField({
  inputRef,
  value,
  placeholder,
  controls,
  onChange,
  onClear,
}: {
  inputRef: RefObject<HTMLInputElement>;
  value: string;
  placeholder: string;
  controls: string;
  onChange: (value: string) => void;
  onClear: () => void;
}) {
  return (
    <div className="global-search__bar">
      <span className="global-search__icon">
        <SearchIcon />
      </span>
      <input
        ref={inputRef}
        className="global-search__input"
        type="search"
        value={value}
        placeholder={placeholder}
        aria-label="Search this site"
        aria-controls={controls}
        onChange={(event) => onChange(event.target.value)}
      />
      {value && (
        // Resets the debounced term as well as the field, which the browser's own
        // type=search affordance would not do.
        <button type="button" className="global-search__clear" aria-label="Clear search" onClick={onClear}>
          <CloseIcon size={12} />
        </button>
      )}
    </div>
  );
}

type RenderedSection = {
  key: string;
  label: string;
  cards: { key: string; node: ReactNode }[];
};

/**
 * Each type is drawn with the card it already owns elsewhere in the system — `ClubCard`,
 * `PlayerCard`, `ArticleCard`, `PageCard` — so a club reads like a club everywhere on the site and
 * a mixed row still scans as one list. Bespoke result cards were the wrong call: they drift from
 * the real ones the moment either changes, which is the failure `packages/ui/CLAUDE.md` calls out.
 *
 * Deep links differ by type: clubs and articles by slug, players by id — players carry a slug
 * (SC-1108) but no `/players/:slug` route exists.
 */
function buildSections(
  results: SearchResults,
  pages: PageSearchResult[],
  variant: 'drawer' | 'pane',
  onNavigate?: () => void
): RenderedSection[] {
  return [
    {
      key: 'clubs',
      label: 'Clubs',
      cards: results.clubs.items.map((club) => ({
        key: club.id,
        // No wrapping link: `ClubCard` renders its own anchor, and an anchor inside an anchor is
        // invalid — the browser closes the outer one early and ships two half-broken links.
        node: <ClubCard club={club} onNavigate={onNavigate} />,
      })),
    },
    {
      key: 'players',
      label: 'Players',
      cards: results.players.items.map((player) => ({
        key: player.id,
        node: (
          <Link className="global-search__card-link" to={`/players/${player.id}`} onClick={onNavigate}>
            {/* Stat columns only where there are numbers to show: a name match carries no
                season stats, and the card omits the row rather than printing zeroes. */}
            <PlayerCard player={player} stats={Object.keys(player.stats ?? {}).length > 0 ? PLAYER_STATS : []} />
          </Link>
        ),
      })),
    },
    {
      key: 'news',
      label: 'News',
      cards: results.news.items.map((article) => ({
        key: article.id,
        node: (
          <ArticleCard
            article={article as Article}
            variant={ArticleVariant.Stacked}
            // A result is a way in, not the story. The summary made every news card two-thirds
            // body copy and set the height of the whole rail, so it stays off on both surfaces —
            // headline, tag and date are what identify an article at a glance.
            showSummary={false}
            showAuthor={false}
            // The drawer is narrow enough that the headline alone has to carry the card.
            showTag={variant !== 'drawer'}
            showDate={variant !== 'drawer'}
            onNavigate={onNavigate}
          />
        ),
      })),
    },
    {
      key: 'pages',
      label: 'Pages',
      cards: pages.map((page) => ({
        key: page.path,
        node: (
          <PageCard
            title={page.title}
            // The breadcrumb, falling back to the path — "Stats › Players" says where a page
            // lives in a way "/stats/players" does not.
            path={page.trail ?? page.path}
            logo={SITE_LOGO}
            href={page.path}
            sameTab={page.sameTab}
            onNavigate={onNavigate}
          />
        ),
      })),
    },
  ];
}

function renderBody({
  variant,
  isActive,
  isLoading,
  isError,
  hasResults,
  total,
  isTotalBounded,
  term,
  sections,
  searchTerm,
}: {
  variant: 'drawer' | 'pane';
  isActive: boolean;
  isLoading: boolean;
  isError: boolean;
  hasResults: boolean;
  total: number;
  isTotalBounded: boolean;
  term: string;
  // Already-built cards: the variant and the navigate handler are baked in by `buildSections`,
  // which is where the per-type card choices live.
  sections: RenderedSection[];
  searchTerm: string;
}) {
  // Below the minimum: never a spinner, never a partial list.
  //
  // The two surfaces answer this differently because what sits under the field differs. The drawer
  // still has the nav list below it — the host only swaps that out once the search goes active — so
  // anything more than a single hint line pushes the site's primary navigation off the screen. The
  // pane has nothing under the field at all, and a blank white column reads as a panel that failed
  // to load rather than one waiting for input, so it gets a resting state of its own.
  if (!isActive) {
    if (variant === 'drawer') {
      return searchTerm.length > 0 ? (
        <p className="global-search__hint small-text">{`Keep typing — search starts at ${SEARCH_MIN_CHARS} characters.`}</p>
      ) : null;
    }
    return (
      <div className="global-search__results">
        <div className="global-search__empty">
          <span className="global-search__empty-icon">
            <SearchIcon size={32} />
          </span>
          {/* A heading, not a styled paragraph: it is the only labelled content in an otherwise
              empty pane, and the results view below titles its sections with real `h3`s. */}
          <h2 className="global-search__empty-message">Search the site</h2>
          {/* One block covers both pre-search states, so typing the first character swaps a line
              of copy rather than replacing the whole panel under the reader. */}
          <p className="global-search__empty-hint small-text">
            {searchTerm.length > 0
              ? `Keep typing — search starts at ${SEARCH_MIN_CHARS} characters.`
              : 'Clubs, players, news and pages — start typing to see results.'}
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="global-search__results" aria-busy="true">
        <SkeletonLoader count={SKELETON_CARD_COUNT} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="global-search__results">
        <p className="global-search__empty-message">Search is unavailable right now. Please try again.</p>
      </div>
    );
  }

  if (!hasResults) {
    return (
      <div className="global-search__results">
        <div className="global-search__empty">
          <span className="global-search__empty-icon">
            <SearchIcon size={32} />
          </span>
          <h2 className="global-search__empty-message">{`No results for “${term}”`}</h2>
          <p className="global-search__empty-hint small-text">Check the spelling or try a different term.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="global-search__results">
      {/* The match total, not the number of cards shown — a search can match more than the cap. */}
      <p className="global-search__count text-3xs">{`${formatTotal(total, isTotalBounded)} result${total === 1 ? '' : 's'} for “${term}”`}</p>
      {sections.map((section) =>
        section.cards.length === 0 ? null : (
          <section key={section.key} className={`global-search__section global-search__section--${section.key}`}>
            <h3 className="global-search__section-title text-3xs">{section.label}</h3>
            {/* A rail: a scrolling row when the results overrun the surface, a plain row when they
                fit. The slide class is the design's own hook — the per-section widths live in
                global-search.scss and differ by result type. */}
            <div className="global-search__rail">
              {section.cards.map((card) => (
                <div key={card.key} className="article-carousel__slide">
                  {card.node}
                </div>
              ))}
            </div>
          </section>
        )
      )}
    </div>
  );
}

/**
 * The one sentence a screen reader is told about the state of the search, matching whatever
 * `renderBody` has drawn. Empty for an untouched field: there is nothing to report until the reader
 * has done something.
 */
function buildStatusMessage({
  isActive,
  isLoading,
  isError,
  hasResults,
  total,
  isTotalBounded,
  term,
  searchTerm,
}: {
  isActive: boolean;
  isLoading: boolean;
  isError: boolean;
  hasResults: boolean;
  total: number;
  isTotalBounded: boolean;
  term: string;
  searchTerm: string;
}): string {
  if (!isActive) {
    return searchTerm.length > 0 ? `Keep typing — search starts at ${SEARCH_MIN_CHARS} characters.` : '';
  }
  if (isLoading) {
    return 'Searching…';
  }
  if (isError) {
    return 'Search is unavailable right now.';
  }
  if (!hasResults) {
    return `No results for ${term}`;
  }
  return `${formatTotal(total, isTotalBounded)} result${total === 1 ? '' : 's'} for ${term}`;
}

/**
 * The API bounds each section's count so an unindexed filter cannot become an unbounded collection
 * scan, so a section reporting exactly the ceiling may have more behind it. The line shows a
 * cross-section sum, so the ceiling has to be tested per section and not against the sum — three
 * sections at 100 add to 300, which is a real number and not "100+".
 */
function formatTotal(total: number, isBounded: boolean): string {
  return isBounded ? `${total}+` : String(total);
}

function CloseIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}
